import { useLayoutEffect, useRef } from 'react';
import { ChartViz, DataQueryInput } from '/@services/generated/graphql-types';
import {
    buildInitialDataQueryForTSHostedCharts,
    shouldPrefetchData,
} from '/@utils/dataQueryForTSHostedCharts.util';

interface usePrefetchCustomChartDataParams {
    chartVizOriginal: ChartViz | null;
    getDataByQuery: (
        dataQueryInput: DataQueryInput,
        vizId: string,
        refetchData: boolean,
        trackDataQueryEvent: (
            dataQueryInput: DataQueryInput,
            totalRowCount: number,
        ) => void,
    ) => Promise<any>;
    trackDataQueryEventForCustomChart: (
        dataQueryInput: DataQueryInput,
        totalRowCount: number,
    ) => void;
}

interface usePrefetchCustomChartDataReturn {
    prefetchPromisesRef: React.MutableRefObject<Map<string, Promise<any>>>;
    prefetchErrorsRef: React.MutableRefObject<Map<string, any>>;
    getQuerySignature: (
        dataQueryInput: DataQueryInput,
        vizId: string,
    ) => string;
}

// 🔧 Module-level cache that persists across component unmount/remount cycles
// This ensures prefetch data survives filter mutations and other operations that cause component unmounting
const globalPrefetchPromises = new Map<string, Promise<any>>();

// 🔧 Module-level error cache to store failed prefetch errors
// This prevents redundant API calls for queries that have already failed
const globalPrefetchErrors = new Map<string, any>();

/**
 * Clear prefetch cache for a specific chart or all charts
 * Call this when chart data becomes stale (e.g., filter changes that require refetch)
 * @param chartVizId - If provided, only clear cache for this chart. If omitted, clear all.
 */
export const clearPrefetchCustomChartCache = (chartVizId?: string) => {
    if (chartVizId) {
        // Clear promises and errors for this chart
        const keysToDelete = new Set<string>();
        globalPrefetchPromises.forEach((_, key) => {
            if (key.startsWith(chartVizId)) {
                keysToDelete.add(key);
            }
        });
        globalPrefetchErrors.forEach((_, key) => {
            if (key.startsWith(chartVizId)) {
                keysToDelete.add(key);
            }
        });
        keysToDelete.forEach(key => {
            globalPrefetchPromises.delete(key);
            globalPrefetchErrors.delete(key);
        });
    } else {
        // Clear entire cache
        globalPrefetchPromises.clear();
        globalPrefetchErrors.clear();
    }
};

/**
 * Custom hook to handle early data prefetch for custom/TS-hosted charts
 *
 * 🚀 PERFORMANCE OPTIMIZATION:
 * - Uses useLayoutEffect + chartVizOriginal to trigger IMMEDIATELY when Apollo data arrives
 * - This is faster than useEffect + chartViz which waits for useMemo + React render
 * - Prevents duplicate prefetches using a cache
 * - Stores in-flight promises to handle race conditions with iframe loading
 */
export const usePrefetchCustomChartData = ({
    chartVizOriginal,
    getDataByQuery,
    trackDataQueryEventForCustomChart,
}: usePrefetchCustomChartDataParams): usePrefetchCustomChartDataReturn => {
    // Use refs to expose the global caches to consumers
    // The global caches persist across component unmount/remount cycles
    const prefetchPromisesRef = useRef(globalPrefetchPromises);
    prefetchPromisesRef.current = globalPrefetchPromises;

    const prefetchErrorsRef = useRef(globalPrefetchErrors);
    prefetchErrorsRef.current = globalPrefetchErrors;

    // Track which chart ID has been prefetched in this container lifecycle
    // This ensures prefetch happens only once per chart, not on every re-render
    const prefetchedChartIdRef = useRef<string | null>(null);

    // Helper to create a unique signature for a data query
    // Used to match iframe requests with in-flight prefetch promises
    const getQuerySignature = (
        dataQueryInput: DataQueryInput,
        vizId: string,
    ): string => {
        const columnIds = dataQueryInput.queries[0]?.query
            .map(col => col.id)
            .sort()
            .join(',');
        return `${vizId}:${columnIds}:${dataQueryInput.queries.length}`;
    };

    // Prefetch execution logic
    const executePrefetch = (chartVizData: ChartViz) => {
        const defaultQuery = buildInitialDataQueryForTSHostedCharts(
            chartVizData,
        );
        if (!defaultQuery) {
            return;
        }

        // Create query signature for tracking in-flight prefetch
        const querySignature = getQuerySignature(defaultQuery, chartVizData.id);
        if (globalPrefetchPromises.has(querySignature)) {
            return;
        }

        // Prefetch in background - this will populate the Apollo cache
        // Store the promise so iframe can wait for it if needed
        const prefetchPromise = getDataByQuery(
            defaultQuery,
            chartVizData.id,
            false, // Don't force refetch, use cache if available
            trackDataQueryEventForCustomChart,
        )
            .then(data => {
                // Clean up the promise from tracking
                globalPrefetchPromises.delete(querySignature);
                // Clear any cached error since this succeeded
                globalPrefetchErrors.delete(querySignature);
                return data;
            })
            .catch(error => {
                // Clean up the promise from tracking
                globalPrefetchPromises.delete(querySignature);
                // Cache the error to prevent redundant API calls
                globalPrefetchErrors.set(querySignature, error);
                // Re-throw to allow awaiting code to handle it
                throw error;
            });

        // Store the promise so iframe can wait for it if it loads before prefetch completes
        globalPrefetchPromises.set(querySignature, prefetchPromise);
    };

    // 🚀 PERFORMANCE OPTIMIZATION: Execute Early Data Prefetch ONCE per container lifecycle
    // Use useLayoutEffect + chartVizOriginal to trigger IMMEDIATELY when Apollo data arrives
    // This is faster than useEffect + chartViz which waits for useMemo + React render
    useLayoutEffect(() => {
        if (!chartVizOriginal) {
            return undefined;
        }

        // Skip if we've already prefetched this chart in this container lifecycle
        if (prefetchedChartIdRef.current === chartVizOriginal.id) {
            return undefined;
        }

        const shouldPrefetch = shouldPrefetchData(chartVizOriginal);
        if (!shouldPrefetch) {
            return undefined;
        }

        // Mark this chart as prefetched
        prefetchedChartIdRef.current = chartVizOriginal.id;

        // Execute prefetch immediately - no React render delay!
        executePrefetch(chartVizOriginal);

        return undefined;
    }, [chartVizOriginal]);

    return {
        prefetchPromisesRef,
        prefetchErrorsRef,
        getQuerySignature,
    };
};
